import React, { useState } from "react";
import {
  Save,
  Sparkles,
  Mail,
  Phone,
  MapPin,
  Clock,
  Plus,
  Trash2,
  Eye,
  Edit3,
  ExternalLink,
  HelpCircle,
  UploadCloud,
  Download,
} from "lucide-react";
import { saveContactContent } from "../../api";
import { useToast } from "../../hooks/use-toast";
import CsvUploadModal from "../../components/admin/CsvUploadModal";
import { toCsv, downloadCsv } from "../../utils/csvHelper";

const ContactContentEditor = ({ initialContent, onUpdate }) => {
  const { toast } = useToast();
  const [csvModalOpen, setCsvModalOpen] = useState(false);
  const [content, setContent] = useState(
    initialContent || {
      heroBadge: "GET IN TOUCH",
      heroTitle: "We'd Love to Hear From You",
      heroSubtitle: "Have a question about a course, feedback on our directory, or want to list your venue? Our team is here to help.",
      email: "contact@minigolfusa.online",
      phone: "(555) 382-4653",
      address: "100 Fairway Blvd, Birmingham, AL 35203, USA",
      hours: "Monday – Friday: 9:00 AM – 6:00 PM EST",
      responseTime: "We typically respond within 24 business hours.",
      faqTitle: "Frequently Asked Questions",
      faqs: [
        {
          q: "How can I add or update my mini golf course listing?",
          a: "You can reach out through our contact form with your venue name, address, website, and operating hours. Our editorial team will verify the details and publish your listing.",
        },
      ],
    }
  );

  const [activeTab, setActiveTab] = useState("edit");
  const [saving, setSaving] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await saveContactContent(content);
      setContent(saved);
      if (onUpdate) onUpdate(saved);
      toast({
        title: "Contact Page Saved",
        description: "Your contact details and FAQs have been updated.",
      });
    } catch (err) {
      toast({
        title: "Save Failed",
        description: err.message || "Failed to update contact content.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleExportCsv = () => {
    const rows = [];
    (content.faqs || []).forEach((faq) => {
      rows.push({ type: "faq", question_or_field: faq.q, answer_or_value: faq.a });
    });
    ["email", "phone", "address", "hours", "responseTime"].forEach((key) => {
      if (content[key]) {
        rows.push({ type: "info", question_or_field: key, answer_or_value: content[key] });
      }
    });

    const cols = [
      { key: "type", label: "Type" },
      { key: "question_or_field", label: "Question or Field" },
      { key: "answer_or_value", label: "Answer or Value" },
    ];
    const csv = toCsv(rows, cols);
    downloadCsv("contact_faqs_and_info.csv", csv);
    toast({ title: "Contact CSV Exported", description: `Exported ${rows.length} rows to CSV.` });
  };

  const handleImportCsv = async (validItems, rawRows, mode) => {
    const faqItems = validItems.filter((item) => item.type === "faq");
    const infoItems = validItems.filter((item) => item.type === "info");

    const newFaqs = faqItems.map((item) => ({
      q: item.question_or_field,
      a: item.answer_or_value,
    }));

    const infoUpdates = {};
    infoItems.forEach((item) => {
      const k = item.question_or_field.toLowerCase().replace(/[^a-z]/g, "");
      if (k === "email") infoUpdates.email = item.answer_or_value;
      else if (k === "phone" || k === "telephone") infoUpdates.phone = item.answer_or_value;
      else if (k === "address" || k === "location") infoUpdates.address = item.answer_or_value;
      else if (k === "hours" || k === "businesshours") infoUpdates.hours = item.answer_or_value;
      else if (k === "responsetime" || k === "response") infoUpdates.responseTime = item.answer_or_value;
    });

    setContent((prev) => {
      const updatedFaqs =
        newFaqs.length > 0
          ? mode === "replace"
            ? newFaqs
            : [...(prev.faqs || []), ...newFaqs]
          : prev.faqs;

      return {
        ...prev,
        ...infoUpdates,
        faqs: updatedFaqs,
      };
    });

    toast({
      title: "Contact & FAQs Updated",
      description: "Imported CSV elements. Click 'Save Changes' to make them live.",
    });
  };

  const handleAddFaq = () => {
    setContent({
      ...content,
      faqs: [
        ...(content.faqs || []),
        { q: "New Question Title?", a: "Detailed answer explaining the process or information." },
      ],
    });
  };

  const handleRemoveFaq = (index) => {
    setContent({
      ...content,
      faqs: content.faqs.filter((_, idx) => idx !== index),
    });
  };

  const handleFaqChange = (index, field, value) => {
    const updated = [...content.faqs];
    updated[index] = { ...updated[index], [field]: value };
    setContent({ ...content, faqs: updated });
  };

  const inputCls =
    "w-full px-3.5 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e5243b] bg-white";

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Contact Page Settings</h1>
          <p className="text-xs text-gray-500 mt-1">
            Configure contact info, office address, operating hours, and FAQ knowledge base
          </p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/contact"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-xl transition-colors shadow-sm"
          >
            <ExternalLink className="w-3.5 h-3.5" /> View Live Page
          </a>
          <button
            type="button"
            onClick={() => setActiveTab(activeTab === "edit" ? "preview" : "edit")}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-xl transition-colors shadow-sm"
          >
            {activeTab === "edit" ? <Eye className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
            {activeTab === "edit" ? "Preview Layout" : "Edit Details"}
          </button>

          <button
            type="button"
            onClick={handleExportCsv}
            title="Export FAQs & info to CSV"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-gray-700 bg-white border border-gray-200 hover:border-gray-300 rounded-xl transition-colors shadow-sm"
          >
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>

          <button
            type="button"
            onClick={() => setCsvModalOpen(true)}
            title="Import FAQs & info from Excel, CSV, TSV, or JSON"
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors shadow-sm"
          >
            <UploadCloud className="w-3.5 h-3.5" /> Import (Excel / CSV)
          </button>

          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] rounded-xl shadow-md transition-all active:scale-[0.98] disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>

      {activeTab === "preview" ? (
        <div className="bg-[#0d0e12] rounded-2xl p-8 text-gray-100 border border-gray-800 shadow-2xl space-y-8">
          <div className="max-w-2xl">
            <div className="inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-white/10 text-white border border-white/20 mb-3">
              {content.heroBadge}
            </div>
            <h2 className="text-3xl font-black text-white">{content.heroTitle}</h2>
            <p className="text-gray-300 text-sm mt-3">{content.heroSubtitle}</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-[#181a22] border border-gray-800 rounded-xl p-4">
              <div className="text-xs text-gray-400 font-bold uppercase">Email Us</div>
              <div className="text-sm font-bold text-white mt-1 truncate">{content.email}</div>
            </div>
            <div className="bg-[#181a22] border border-gray-800 rounded-xl p-4">
              <div className="text-xs text-gray-400 font-bold uppercase">Phone</div>
              <div className="text-sm font-bold text-white mt-1">{content.phone}</div>
            </div>
            <div className="bg-[#181a22] border border-gray-800 rounded-xl p-4">
              <div className="text-xs text-gray-400 font-bold uppercase">Address</div>
              <div className="text-sm font-bold text-white mt-1">{content.address}</div>
            </div>
            <div className="bg-[#181a22] border border-gray-800 rounded-xl p-4">
              <div className="text-xs text-gray-400 font-bold uppercase">Hours</div>
              <div className="text-sm font-bold text-white mt-1">{content.hours}</div>
            </div>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSave} className="space-y-8 max-w-4xl">
          {/* Header Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#e5243b]" /> Page Header
            </h2>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Top Badge
              </label>
              <input
                type="text"
                value={content.heroBadge || ""}
                onChange={(e) => setContent({ ...content, heroBadge: e.target.value })}
                placeholder="GET IN TOUCH"
                className={inputCls}
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Headline (H1)
              </label>
              <input
                type="text"
                value={content.heroTitle || ""}
                onChange={(e) => setContent({ ...content, heroTitle: e.target.value })}
                placeholder="We'd Love to Hear From You"
                className={inputCls}
                required
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Subtitle
              </label>
              <textarea
                rows={2}
                value={content.heroSubtitle || ""}
                onChange={(e) => setContent({ ...content, heroSubtitle: e.target.value })}
                className={inputCls}
                required
              />
            </div>
          </div>

          {/* Contact Details Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <h2 className="text-base font-bold text-gray-900 border-b border-gray-100 pb-3">
              Direct Contact Information
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5 text-blue-500" /> Support Email
                </label>
                <input
                  type="email"
                  value={content.email || ""}
                  onChange={(e) => setContent({ ...content, email: e.target.value })}
                  placeholder="contact@minigolfusa.online"
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5 text-emerald-500" /> Phone Number
                </label>
                <input
                  type="text"
                  value={content.phone || ""}
                  onChange={(e) => setContent({ ...content, phone: e.target.value })}
                  placeholder="(555) 382-4653"
                  className={inputCls}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-red-500" /> Physical / Headquarters Address
                </label>
                <input
                  type="text"
                  value={content.address || ""}
                  onChange={(e) => setContent({ ...content, address: e.target.value })}
                  placeholder="100 Fairway Blvd, Birmingham, AL 35203, USA"
                  className={inputCls}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5 flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-amber-500" /> Operating / Support Hours
                </label>
                <input
                  type="text"
                  value={content.hours || ""}
                  onChange={(e) => setContent({ ...content, hours: e.target.value })}
                  placeholder="Monday – Friday: 9:00 AM – 6:00 PM EST"
                  className={inputCls}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                Response Guarantee Note
              </label>
              <input
                type="text"
                value={content.responseTime || ""}
                onChange={(e) => setContent({ ...content, responseTime: e.target.value })}
                placeholder="We typically respond within 24 business hours."
                className={inputCls}
              />
            </div>
          </div>

          {/* FAQs Manager Card */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-5">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <HelpCircle className="w-4 h-4 text-[#e5243b]" /> FAQ Section ({(content.faqs || []).length} Questions)
              </h2>
              <button
                type="button"
                onClick={handleAddFaq}
                className="inline-flex items-center gap-1 text-xs font-bold text-[#e5243b] hover:text-[#cf1c31]"
              >
                <Plus className="w-3.5 h-3.5" /> Add Question
              </button>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                FAQ Section Title
              </label>
              <input
                type="text"
                value={content.faqTitle || ""}
                onChange={(e) => setContent({ ...content, faqTitle: e.target.value })}
                placeholder="Frequently Asked Questions"
                className={inputCls}
                required
              />
            </div>

            <div className="space-y-4">
              {(content.faqs || []).map((faq, idx) => (
                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-4 relative space-y-3">
                  <button
                    type="button"
                    onClick={() => handleRemoveFaq(idx)}
                    className="absolute top-3 right-3 text-gray-400 hover:text-red-500 p-1 rounded-lg"
                    title="Delete question"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>

                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                      Question #{idx + 1}
                    </label>
                    <input
                      type="text"
                      value={faq.q}
                      onChange={(e) => handleFaqChange(idx, "q", e.target.value)}
                      placeholder="e.g. How can I submit a new mini golf course?"
                      className="w-full px-3 py-1.5 text-xs font-bold border border-gray-300 rounded-lg bg-white"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1">
                      Answer
                    </label>
                    <textarea
                      rows={2}
                      value={faq.a}
                      onChange={(e) => handleFaqChange(idx, "a", e.target.value)}
                      placeholder="Provide the answer..."
                      className="w-full px-3 py-1.5 text-xs text-gray-800 border border-gray-300 rounded-lg bg-white leading-relaxed"
                      required
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="px-8 py-3 rounded-xl font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] shadow-lg shadow-red-900/20 transition-all flex items-center gap-2"
            >
              <Save className="w-4 h-4" /> {saving ? "Saving Changes..." : "Save Contact Settings"}
            </button>
          </div>
        </form>
      )}

      <CsvUploadModal
        isOpen={csvModalOpen}
        onClose={() => setCsvModalOpen(false)}
        sectionKey="contact"
        onImport={handleImportCsv}
        allowModeSwitch={true}
      />
    </div>
  );
};

export default ContactContentEditor;
